import ReactFinalForm from 'components/form/ReactFinalForm';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { IOption, IPurchaseOrder, ITransportActiveDocument } from 'interfaces';
import { IdentityField } from 'components/form/IdentityField';
import { useTranslation } from 'react-i18next';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import { Button } from '@mui/material';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { useEffect, useState } from 'react';
import { orderService, supplierService, warehouseService } from 'services/api';

interface TransportActiveDocumenFormParams {
  transport_document: ITransportActiveDocument;
  onSubmit: any;
  isUpdate: boolean;
  isSupplierDdt?: boolean;
  disableEdit?: boolean;
}

export const TransportActiveDocumentForm = (params: TransportActiveDocumenFormParams) => {
  const { transport_document, onSubmit, isUpdate, disableEdit, isSupplierDdt } = params;
  const { t } = useTranslation();

  const [orders, setOrders] = useState<IOption[]>([]);
  const [warehouses, setWarehouses] = useState<IOption[]>([]);
  const [suppliers, setSuppliers] = useState<IOption[]>([]);
  const [selectedWarehouseOut, setSelectedWarehouseOut] = useState<string | null>(
    String(transport_document?.warehouse_out ?? '')
  );

  // --------------------------------------------- GETTERS AND USE EFFECTS

  /**
   * useEffect per il caricamento iniziale dei dati di configurazione globale
   * Gestisce sia il caricamento dei magazzini che dei fornitori in base al contesto operativo.
   */
  useEffect(() => {
    warehouseService.getAllBaseInformation().then(setWarehouses);
    if (isSupplierDdt) {
      supplierService.getAllBaseInformation().then(setSuppliers);
    }
  }, []);

  /**
   * useEffect per il caricamento automatico degli ordini quando viene selezionato un magazzino
   */
  useEffect(() => {
    if (selectedWarehouseOut) {
      fetchOrders();
    }
  }, [selectedWarehouseOut]);

  /**
   * Funzione asincrona che recupera gli ordini disponibili per la creazione di documenti di trasporto.
   * Gestisce sia il caricamento standard che il mantenimento dell'ordine selezionato anche se
   * non presente nella lista filtrata.
   */
  const fetchOrders = () => {
    const params = {
      // 2025-02-26: Introduzione dei Trasferimenti tra magazzini ORA OGNI ORDINE PUò ESSERE SCELTO come valido
      //              nascondo la condizione di visibilità per "codice magazzino"
      //warehouse: selectedWarehouseOut['code'] ?? '',
      status: 1
    };

    orderService.openOrdersNotInDDTactive(1000, 0, params).then((res) => {
      const fetchedOrders = res.results.map((value) => ({
        id: value.id,
        code: value.ref,
        description: value.description
      }));

      // Se esiste un ordine selezionato ma non è nella lista, aggiungilo
      const existingOrderId = transport_document.order;
      const alreadyIncluded = fetchedOrders.some((o) => o.id === existingOrderId);

      if (!alreadyIncluded && existingOrderId) {
        orderService.get(existingOrderId).then((order) => {
          const existingOrder = {
            id: order.id,
            code: order.ref,
            description: order.ref + ' - ' + order.supplier
          };
          setOrders([...fetchedOrders, existingOrder]);
        });
      } else {
        setOrders(fetchedOrders);
      }
    });
  };

  return (
    <div>
      {transport_document && orders && (
        <ReactFinalForm
          onSubmit={disableEdit ? {} : onSubmit}
          edit={!!transport_document.id}
          hideControls={true}
          disableEdit={disableEdit}
          formId="amountForm">
          <div className="w-full">
            {/* Prima riga: Supplier e Warehouses (out e in) */}

            <div id="prima_riga" className="flex w-full gap-4 mb-4">
              <div id="warehouse_out" className="flex-1">
                <IdentityField
                  name="warehouse_out"
                  label={t(`global.warehouse_out`)}
                  initialValue={transport_document.warehouse_out}
                  initValue={warehouses.find((s) => s.id === transport_document.warehouse_out)}
                  component={AutocompleteAdapter}
                  options={warehouses}
                  required
                  disabled={disableEdit || !!transport_document.warehouse_out}
                  type="text"
                  // this should controll the fetch of orders
                  setValue={setSelectedWarehouseOut}
                  service={warehouseService}
                  submitObject
                />
              </div>
              {isSupplierDdt ? (
                <div id="supplier" className="flex-1">
                  <IdentityField
                    name="supplier"
                    label={t(`global.supplier`)}
                    initialValue={transport_document.supplier}
                    initValue={suppliers.find((s) => s.id === transport_document.supplier)}
                    component={AutocompleteAdapter}
                    options={suppliers}
                    optionLabel="company_name"
                    required
                    disabled={disableEdit || !!transport_document.supplier}
                    type="text"
                  />
                </div>
              ) : (
                <div id="warehouse_in" className="flex-1">
                  <IdentityField
                    name="warehouse_in"
                    label={t(`global.warehouse_in`)}
                    initialValue={transport_document.warehouse_in}
                    initValue={warehouses.find((s) => s.id === transport_document.warehouse_in)}
                    component={AutocompleteAdapter}
                    options={warehouses}
                    required
                    disabled={disableEdit || !!transport_document.warehouse_in}
                    type="text"
                  />
                </div>
              )}
              <div id="order" className="flex-1">
                <IdentityField
                  name="order"
                  label={t(`global.order`)}
                  initialValue={transport_document.order}
                  initValue={orders.find((o) => o.id === transport_document.order)}
                  component={AutocompleteAdapter}
                  options={orders}
                  optionLabel="description"
                  optionValue="id"
                  disabled={
                    disableEdit ||
                    !!transport_document.order ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  autoSelect={false}
                />
              </div>
            </div>

            {/* Seconda riga: Ref, Date */}

            <div id="seconda_riga" className="flex w-full gap-4 mb-4 flex-wrap">
              <div id="ref" className="flex-1">
                <IdentityField
                  name="ref"
                  label={
                    isSupplierDdt ? t(`pages.supplier-active-ddt.ref`) : t(`pages.active-ddt.ref`)
                  }
                  initialValue={transport_document?.ref ?? ''}
                  disabled={
                    disableEdit ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
              <div id="date" className="flex-1">
                <IdentityField
                  name="date"
                  label={
                    isSupplierDdt ? t(`pages.supplier-active-ddt.date`) : t(`pages.active-ddt.date`)
                  }
                  component={DatePickerAdapter}
                  type="date"
                  disabled={
                    disableEdit ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  initialValue={transport_document?.date ?? null}
                  noFuture={true}
                />
              </div>
            </div>

            {/* Terza riga: Peso totale & Colli N. */}

            <div id="terza_riga" className="flex w-full gap-4 mb-4">
              <div id="colli" className="flex-1">
                <IdentityField
                  name="colli"
                  label={t(`pages.active-ddt.colli`)}
                  initialValue={transport_document?.colli ?? ''}
                  disabled={
                    disableEdit ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
              <div id="weight" className="flex-1">
                <IdentityField
                  name="weight"
                  label={t(`pages.active-ddt.weight`)}
                  initialValue={transport_document?.weight ?? ''}
                  disabled={
                    disableEdit ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
            </div>

            {/* Quarta riga: Note */}

            <div id="quarta_riga" className="flex w-full gap-4 mb-4">
              <div id="notes" className="flex-1 w-full">
                <IdentityField
                  name="notes"
                  label={t(`global.notes`)}
                  initialValue={transport_document?.notes ?? ''}
                  disabled={
                    disableEdit ||
                    ((transport_document?.status === 'SENT' ||
                      transport_document?.status === 'ARRIVED') &&
                      isUpdate)
                  }
                  component={TextFieldAdapter}
                  type="text"
                />
              </div>
            </div>

            {/* Quinta riga: Pulsante Save */}

            {!disableEdit && (
              <div className="flex justify-end">
                <Button variant="contained" type="submit" form="amountForm" color="primary">
                  {t(`global.save`)}
                </Button>
              </div>
            )}
          </div>
        </ReactFinalForm>
      )}
    </div>
  );
};

export default TransportActiveDocumentForm;
